import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useBookingFlowType } from '../../features/orders/list/actions/book-order/use-booking-flow-type.hook';
import { useQueryGetProject } from '../queries/projects.queries';
import { BookingFlowType } from '../../ui/enums/booking-flow-type.enum';

export interface ProjectContextValue {
  // Core project data
  projectId: string;
  project: ReturnType<typeof useQueryGetProject>['data'];
  isLoading: boolean;
  // Flow-specific configuration
  bookingFlowType: BookingFlowType;
}

export const ProjectContext = createContext<ProjectContextValue | null>(null);

/**
 * The project context for callers that render with or without one — `Layout` is mounted on the
 * invitation error screen and while the shortcode resolves, neither of which has a project.
 */
export function useOptionalCurrentProject(): ProjectContextValue | null {
  return useContext(ProjectContext);
}

/** The project context where one is required. Throws rather than rendering half a page without it. */
export function useCurrentProject(): ProjectContextValue {
  const context = useOptionalCurrentProject();
  if (!context) {
    throw new Error('useCurrentProject must be used within ProjectProvider');
  }
  return context;
}

export function ProjectProvider({ projectId, children }: { projectId: string; children: ReactNode }) {
  const projectQuery = useQueryGetProject(projectId);
  const bookingFlowType = useBookingFlowType(projectId);

  const value = useMemo((): ProjectContextValue => {
    return {
      projectId,
      project: projectQuery.data,
      isLoading: projectQuery.isLoading,
      bookingFlowType
    };
  }, [projectId, projectQuery.data, projectQuery.isLoading, bookingFlowType]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}
